import axios from '@/core/axios'
import { ShippingAddressDto } from '@/domain/shared/dtos/shipping-address.dto'
import { ShippingAddressSchema } from '@/domain/shared/schemas/shipping-address.schema'
import { Customer } from 'stoux-shared-types/models/customer'

import { UpdateCustomerDto } from '../dtos/update-customer.dto'
import { UpdatePasswordDto } from '../dtos/update-password.dto'
import { UpdateShippingAddressDto } from '../dtos/update-shipping-address.dto'
import { CustomerRepository } from '../repositories/customer'
import { UpdateCustomerSchema } from '../schemas/update-customer.schema'
import { UpdateShippingAddressSchema } from '../schemas/update-shipping-address.schema'

type InjectedDependencies = {
    customerRepository: CustomerRepository
}

export class CustomerService {
    private readonly _customerRepository: CustomerRepository

    constructor({ customerRepository }: InjectedDependencies) {
        this._customerRepository = customerRepository
    }

    async updateCustomer(dto: UpdateCustomerDto) {
        const validatedDto = await UpdateCustomerSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error('Invalid customer')
        }

        return this._customerRepository.updateCustomer(dto)
    }

    async updatePassword(dto: Pick<UpdatePasswordDto, 'password'>) {
        const { data } = await axios.post<{ customer: Customer }>(
            '/customers/me',
            dto,
        )
        return data
    }

    public async createShippingAddress(
        dto: ShippingAddressDto,
    ): Promise<Customer> {
        const validatedDto = await ShippingAddressSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error('Invalid shipping address')
        }

        return this._customerRepository.addShippingAddress(validatedDto.data)
    }

    public async updateShippingAddress(
        dto: UpdateShippingAddressDto,
    ): Promise<Customer> {
        const validatedDto =
            await UpdateShippingAddressSchema.safeParseAsync(dto)

        if (!validatedDto.success) {
            console.error('UpdateShippingAddress - Invalid DTO')
            throw new Error('Invalid shipping address')
        }

        return this._customerRepository.updateShippingAddress(validatedDto.data)
    }

    public async generateResetPassword(dto: { email: string }) {
        const { data } = await axios.post('/customers/password-token', dto)
        return data
    }

    public async resetPassword(dto: ResetPasswordDto) {
        const { data } = await axios.post('/customers/password-reset', dto)
        return data
    }
}

export type ResetPasswordDto = {
    email: string
    token: string
    password: string
}
